import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Calendar, CheckCircle, Loader2, User, ShoppingCart, Plus, Minus, Trash2,
  MessageCircle, ChevronRight, ChefHat, UtensilsCrossed,
  Phone, Facebook, MapPin, Mail, Heart, Sparkles, Users
} from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import LeverageSection from '../components/LeverageSection';
import { apiFetch } from '../lib/api';
import {
  trackProductView,
  trackAddToCart,
  trackBeginCheckout,
  trackOrderCreated,
  trackWhatsAppClick,
} from '../lib/analytics';
import { DEFAULT_HOME_SECTIONS, type HomePage, type HomePageSection } from '../lib/homeDefaults';
import type { HeroSection, MissionSection, AboutSection, LeverageSectionData, FooterSection } from './BrandStory';

const BUSINESS_PHONE = '85260771493';

type DeliveryRegion = 'HK_ISLAND' | 'KOWLOON' | 'NT';

function linkifyDescription(text: string): React.ReactNode[] {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.startsWith('http://') || part.startsWith('https://')) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-[#C2410C] hover:text-[#9a3412] break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function renderDescription(text?: string) {
  if (!text) return null;
  return text.split('\n').map((line, i) => (
    <p key={i} className={i > 0 ? 'mt-3' : ''}>
      {line}
    </p>
  ));
}

function renderHtmlDescription(text?: string) {
  if (!text) return null;
  const html = text
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => `<p class="mb-4 last:mb-0">${p}</p>`)
    .join('');
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

interface ProductItem {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  enabled: boolean;
  stockQuantity: number;
  category: string;
}

interface CartItem {
  productId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

interface DeliveryInfo {
  address: string;
  addressBuilding: string;
  addressFloor: string;
  addressUnit: string;
  deliveryDate: string;
  estate: string;
  name: string;
  contactPhone: string;
  remarks: string;
  region: DeliveryRegion;
}

interface ServiceEstate {
  id: number;
  name: string;
  nameEn?: string;
  region: DeliveryRegion;
  area?: string;
  managementCompany?: string;
  minOrderAmount: number;
  deliveryDay?: string;
}

const DEFAULT_PRODUCTS: ProductItem[] = [];

interface ApiProduct {
  id: number;
  category: string;
  name: string;
  description: string | null;
  price: number | null;
  is_active: number;
  stock_quantity: number;
  sort_order: number;
  image_url: string | null;
}

const DEFAULT_MISSION_VALUES: { title: string; description: string }[] = [];

const CATEGORY_TABS: { key: 'lamb' | 'beef' | 'seafood' | 'drinks'; label: string }[] = [
  { key: 'lamb', label: '羊肉' },
  { key: 'beef', label: '牛肉' },
  { key: 'seafood', label: '海鮮' },
  { key: 'drinks', label: '酒水' },
];

function getSection<T extends HomePageSection['type']>(
  sections: HomePageSection[],
  type: T
): Extract<HomePageSection, { type: T }> | undefined {
  return sections.find((s): s is Extract<HomePageSection, { type: T }> => s.type === type);
}

const OrderLanding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioKey = searchParams.get('scenarioKey') || '';
  const referralCode = searchParams.get('gs');

  const [step, setStep] = useState<'menu' | 'cart' | 'delivery'>('menu');

  const [productsByCategory, setProductsByCategory] = useState<Record<string, ProductItem[]>>({});
  const [activeCategory, setActiveCategory] = useState<'lamb' | 'beef' | 'seafood' | 'drinks'>('beef');
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [lowStockItems, setLowStockItems] = useState<Set<string>>(new Set());

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [cart, setCart] = useState<CartItem[]>([]);

  const [delivery, setDelivery] = useState<DeliveryInfo>({
    address: '', addressBuilding: '', addressFloor: '', addressUnit: '', deliveryDate: '', estate: '', name: '', contactPhone: '', remarks: '', region: 'KOWLOON'
  });
  const [estates, setEstates] = useState<ServiceEstate[]>([]);
  const [minDateStr, setMinDateStr] = useState('');

  const [deliveryPhoneError, setDeliveryPhoneError] = useState('');

  const [campaignConfig, setCampaignConfig] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [homePage, setHomePage] = useState<HomePage | null>(null);

  const cartTotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const allProducts = Object.values(productsByCategory).flat();
  const activeProducts = productsByCategory[activeCategory] || [];
  const menuTotal = allProducts.reduce((sum, p) => {
    const qty = quantities[p.id] || 0;
    return sum + qty * p.price;
  }, 0);
  const menuItemCount = allProducts.reduce((sum, p) => sum + (quantities[p.id] || 0), 0);
  const selectedEstate = estates.find(e => e.name === delivery.estate);
  const minOrderAmount = selectedEstate?.minOrderAmount || 0;
  const shortfall = minOrderAmount > 0 ? Math.max(0, minOrderAmount - menuTotal) : 0;

  const homeSections = homePage?.sections || DEFAULT_HOME_SECTIONS;
  const heroSection = getSection(homeSections, 'hero') || getSection(DEFAULT_HOME_SECTIONS, 'hero')!;
  const missionSection = getSection(homeSections, 'mission');
  const aboutSection = getSection(homeSections, 'about') || getSection(DEFAULT_HOME_SECTIONS, 'about');
  const leverageSection = getSection(homeSections, 'leverage');
  const footerSection = getSection(homeSections, 'footer') || getSection(DEFAULT_HOME_SECTIONS, 'footer');

  useEffect(() => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    const minDateStr = minDate.toLocaleDateString('en-CA');
    setMinDateStr(minDateStr);
    // Default delivery date to two days later unless already set
    setDelivery(prev => prev.deliveryDate ? prev : { ...prev, deliveryDate: minDateStr });
  }, []);

  // Load saved delivery info from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('gs2Delivery');
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedEstate = parsed.estate || '';
        const validRegions: DeliveryRegion[] = ['HK_ISLAND', 'KOWLOON', 'NT'];
        let savedRegion: DeliveryRegion = parsed.region;
        if (!validRegions.includes(savedRegion)) {
          // Map legacy regions or default to KOWLOON
          savedRegion = parsed.region === 'HK' ? 'HK_ISLAND' : 'KOWLOON';
        }
        setDelivery(prev => ({
          ...prev,
          address: parsed.address || '',
          addressBuilding: parsed.addressBuilding || parsed.address || '',
          addressFloor: parsed.addressFloor || '',
          addressUnit: parsed.addressUnit || '',
          estate: savedEstate,
          name: parsed.name || '',
          contactPhone: parsed.contactPhone || parsed.phone || '',
          remarks: parsed.remarks || '',
          region: savedRegion,
        }));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (scenarioKey) {
      apiFetch(`/api/public/campaigns/${scenarioKey}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data && data.config) {
            setCampaignConfig(data.config);
          }
        })
        .catch(() => { /* ignore */ });
    }
  }, [scenarioKey]);

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem('gs_referral_code', referralCode);
      localStorage.setItem('gs2_referral_code', referralCode);
      localStorage.setItem('gs2_referral_ts', Date.now().toString());
    }
  }, [referralCode]);

  // Track category tab changes as product/package views.
  useEffect(() => {
    const tab = CATEGORY_TABS.find(t => t.key === activeCategory);
    if (tab && productsLoaded) {
      trackProductView(activeCategory, tab.label);
    }
  }, [activeCategory, productsLoaded]);

  useEffect(() => {
    apiFetch('/api/public/service-estates')
      .then(r => r.ok ? r.json() : null)
      .then((data: ServiceEstate[] | null) => {
        if (data && Array.isArray(data)) {
          setEstates(data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh-HK')));
        }
      })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    apiFetch('/api/public/products')
      .then(r => r.ok ? r.json() : null)
      .then((data: ApiProduct[] | null) => {
        if (data && Array.isArray(data)) {
          const lowStock = new Set<string>();
          const grouped: Record<string, ProductItem[]> = {};
          data
            .filter(p => p.stock_quantity > 0 && p.is_active === 1)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .forEach((p) => {
              if (p.stock_quantity > 0 && p.stock_quantity < 15) {
                lowStock.add(`i${p.id}`);
              }
              const item: ProductItem = {
                id: `i${p.id}`,
                name: p.name,
                description: p.description || '',
                price: p.price || 0,
                imageUrl: p.image_url || `/dishes/i${p.id}.jpg`,
                enabled: p.is_active === 1,
                stockQuantity: p.stock_quantity,
                category: p.category || 'beef',
              };
              if (!grouped[item.category]) grouped[item.category] = [];
              grouped[item.category].push(item);
            });
          setProductsByCategory(grouped);
          setLowStockItems(lowStock);
        }
        setProductsLoaded(true);
      })
      .catch(() => {
        setProductsByCategory({ beef: DEFAULT_PRODUCTS });
        setProductsLoaded(true);
      });
  }, []);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const res = await apiFetch('/api/public/cms/pages/home');
        if (res.ok) {
          const data = await res.json();
          setHomePage({
            title: data.title || '阿力廚房 Alex Kitchen',
            description: data.description || '',
            sections: Array.isArray(data.sections) && data.sections.length > 0
              ? data.sections
              : DEFAULT_HOME_SECTIONS,
            meta: data.meta || {},
            isActive: data.isActive ?? true,
          });
        } else {
          setHomePage({
            title: '阿力廚房 Alex Kitchen',
            description: '',
            sections: DEFAULT_HOME_SECTIONS,
            meta: {},
            isActive: true,
          });
        }
      } catch {
        setHomePage({
          title: '阿力廚房 Alex Kitchen',
          description: '',
          sections: DEFAULT_HOME_SECTIONS,
          meta: {},
          isActive: true,
        });
      }
    };
    fetchHome();
  }, []);

  useEffect(() => {
    if (homePage?.title) {
      document.title = `${homePage.title} | 阿力廚房 Alex Kitchen`;
    }
  }, [homePage?.title]);

  const findProduct = (productId: string) => allProducts.find(p => p.id === productId);

  const handleQtyChange = (productId: string, delta: number) => {
    const product = findProduct(productId);
    if (delta > 0 && product) {
      trackAddToCart(productId, delta, product.price);
    }
    setQuantities(prev => {
      const current = prev[productId] || 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: next };
    });
  };

  const setQtyDirect = (productId: string, val: number) => {
    const product = findProduct(productId);
    const prevQty = quantities[productId] || 0;
    const v = Math.max(0, val);
    if (product && v > prevQty) {
      trackAddToCart(productId, v - prevQty, product.price);
    }
    setQuantities(prev => {
      if (v === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: v };
    });
  };

  const handleGoToCart = () => {
    const items: CartItem[] = [];
    for (const p of allProducts) {
      const qty = quantities[p.id] || 0;
      if (qty > 0) {
        items.push({
          productId: p.id,
          name: p.name,
          quantity: qty,
          unitPrice: p.price,
          subtotal: p.price * qty,
        });
      }
    }
    if (items.length === 0) return;
    setCart(items);
    trackBeginCheckout(
      items.reduce((sum, i) => sum + i.subtotal, 0),
      items.reduce((sum, i) => sum + i.quantity, 0)
    );
    setStep('cart');
  };

  const handleCartQtyChange = (productId: string, delta: number) => {
    if (delta > 0) {
      const item = cart.find(i => i.productId === productId);
      if (item) {
        trackAddToCart(productId, delta, item.unitPrice);
      }
    }
    setCart(prev => {
      const idx = prev.findIndex(i => i.productId === productId);
      if (idx === -1) return prev;
      const item = prev[idx];
      const nextQty = Math.max(0, item.quantity + delta);
      if (nextQty === 0) {
        return prev.filter(i => i.productId !== productId);
      }
      const updated = [...prev];
      updated[idx] = { ...item, quantity: nextQty, subtotal: nextQty * item.unitPrice };
      return updated;
    });
  };

  const removeCartItem = (productId: string) => {
    setCart(prev => prev.filter(i => i.productId !== productId));
  };

  const handleBackToMenu = () => {
    const q: Record<string, number> = {};
    for (const item of cart) {
      q[item.productId] = item.quantity;
    }
    setQuantities(q);
    setStep('menu');
  };

  const handleDeliveryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDelivery(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setDeliveryPhoneError('');
    setSubmitError('');

    if (cart.length === 0) {
      setSubmitError('請先選擇商品');
      return;
    }
    if (minOrderAmount > 0 && cartTotal < minOrderAmount) {
      setSubmitError(`最低消費為 HK$${minOrderAmount}，尚差 HK$${minOrderAmount - cartTotal}`);
      return;
    }
    if (!delivery.region) {
      setSubmitError('請選擇送貨地區');
      return;
    }

    const address = `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim();
    if (!delivery.addressBuilding || !delivery.addressFloor || !delivery.addressUnit) {
      setSubmitError('請填寫完整詳細地址');
      return;
    }

    const contactDigits = delivery.contactPhone.replace(/\D/g, '');
    if (contactDigits.length !== 8) {
      setDeliveryPhoneError('請輸入有效的 8 位香港手提電話號碼');
      return;
    }

    setIsSubmitting(true);
    trackBeginCheckout(cartTotal, cart.reduce((sum, i) => sum + i.quantity, 0));

    const effectiveRefCode = searchParams.get('gs') || localStorage.getItem('gs_referral_code') || localStorage.getItem('gs2_referral_code') || undefined;

    try {
      const orderItems = cart.map(item => ({
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        subtotal: item.subtotal,
      }));

      const res = await apiFetch('/api/public/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: orderItems,
          totalPrice: cartTotal,
          region: delivery.region,
          address: `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim(),
          estate: delivery.estate,
          deliveryDate: delivery.deliveryDate,
          deliveryTime: '',
          name: delivery.name,
          phone: delivery.contactPhone,
          contactPhone: delivery.contactPhone,
          email: null,
          remarks: delivery.remarks,
          referralCode: effectiveRefCode,
          language: 'zh',
          campaignName: searchParams.get('scenarioKey') || 'gs2-default',
        })
      });

      if (res.ok) {
        const data = await res.json();
        const orderNum = (data.orderNum || "-");
        trackOrderCreated(orderNum, cartTotal);
        // Save delivery info (excluding date) for next order
        localStorage.setItem('gs2Delivery', JSON.stringify({
          address: `${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`.trim(),
          addressBuilding: delivery.addressBuilding,
          addressFloor: delivery.addressFloor,
          addressUnit: delivery.addressUnit,
          estate: delivery.estate,
          name: delivery.name,
          contactPhone: delivery.contactPhone,
          remarks: delivery.remarks,
          region: delivery.region,
        }));

        const whatsAppMsg = `你好，我剛在阿力廚房提交了訂單，會盡快付款，請確認。\n\n訂單號碼：${orderNum}\n總額：HK$${cartTotal}\n送貨地區：${delivery.region === 'HK_ISLAND' ? '香港島' : delivery.region === 'KOWLOON' ? '九龍' : '新界'}\n地址：${delivery.addressBuilding} ${delivery.addressFloor} ${delivery.addressUnit}`;
        const whatsAppLink = `https://wa.me/${BUSINESS_PHONE}?text=${encodeURIComponent(whatsAppMsg)}`;
        // Save the message and link for the success page. Opening WhatsApp from
        // the success page is more reliable than auto-opening from the form,
        // especially on mobile where the browser may otherwise leave the page.
        try {
          sessionStorage.setItem('gs2_last_whatsapp_msg', whatsAppMsg);
          sessionStorage.setItem('gs2_last_whatsapp_link', whatsAppLink);
        } catch { /* ignore */ }

        navigate(`/order/success?orderId=${data.id}&orderNum=${orderNum}&total=${cartTotal}${data.referralCode ? '&refCode=' + data.referralCode : ''}&lang=zh`);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setSubmitError(`提交失敗 (${res.status}): ${errorData.error || '請稍後再試'}`);
      }
    } catch (error) {
      setSubmitError('網絡錯誤，請檢查連接後重試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHeroCta = (url?: string) => {
    if (!url) return;
    if (url.startsWith('#')) {
      const id = url.slice(1);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(url);
    }
  };

  const renderHero = (s: HeroSection) => (
    <div className="relative">
      {/* Hero Image - clean, no text overlay */}
      <section className="relative w-full aspect-video overflow-hidden">
        <div className="absolute inset-0">
          {/* Soft beige fallback — always rendered so there is never a blank/broken background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#F9F6F0] via-[#F2EEE6] to-[#EBE5DB]" />
          {s.backgroundImage && (
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat z-[1]"
              style={{ backgroundImage: `url("${s.backgroundImage}")` }}
            />
          )}
        </div>
      </section>

      {/* Hero text block - placed below the image on all screen sizes */}
      <div className="bg-[#FFF7ED] border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-10 text-center">
          <p className="text-[#C2410C] text-sm md:text-base font-medium mb-2 md:mb-3 tracking-wider">
            {s.subtitle}
          </p>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-stone-800 mb-4 md:mb-5 leading-tight">
            <span dangerouslySetInnerHTML={{ __html: s.title }} />
          </h1>
          <div className="text-base md:text-lg text-stone-600 leading-relaxed mb-5 md:mb-6 max-w-2xl mx-auto">
            {renderDescription(s.description)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <button
              onClick={() => handleHeroCta(s.ctaPrimary.url)}
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 bg-[#C2410C] hover:bg-[#9a3412] text-white font-bold rounded-full transition-colors shadow-lg"
            >
              <ShoppingCart size={16} className="md:w-[18px] md:h-[18px]" />
              {s.ctaPrimary.label || '立即選購'}
            </button>
            <button
              onClick={() => handleHeroCta(s.ctaSecondary.url)}
              className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 border-2 border-[#C2410C] text-[#C2410C] font-bold rounded-full hover:bg-[#C2410C]/5 transition-colors"
            >
              {s.ctaSecondary.label || '了解我們'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMission = (s?: MissionSection) => {
    const values = s?.values || DEFAULT_MISSION_VALUES;
    return (
      <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 text-[#C2410C] font-medium mb-4">
              <Heart size={20} />
              <span>我們相信</span>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
              {s?.title || 'OUR MISSION'}
            </h2>
            <div className="text-stone-600 leading-relaxed">
              {renderDescription(s?.description)}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="text-[#C2410C]" size={24} />
                </div>
                <h3 className="text-lg font-bold text-stone-800 mb-3">{v.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderAbout = (s?: AboutSection) => {
    if (!s) return null;
    return (
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-[#C2410C] font-medium mb-4">
                <Users size={20} />
                <span>關於我們</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">{s.title}</h2>
              <div className="text-stone-600 leading-relaxed mb-8">
                {renderHtmlDescription(s.description)}
              </div>
              <button
                onClick={() => navigate('/about')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C2410C] text-white font-bold rounded-full hover:bg-[#9a3412] transition-colors shadow-lg"
              >
                了解更多
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-stone-100">
                {s.imageUrl ? (
                  <img
                    src={s.imageUrl}
                    alt={s.imageAlt}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <ChefHat size={48} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderWhatsAppCta = () => (
    <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
      <div className="max-w-4xl mx-auto text-center">
        <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto mb-6 text-white shadow-lg">
          <MessageCircle size={32} />
        </div>
        <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
          有任何查詢？歡迎 WhatsApp 我們
        </h2>
        <p className="text-stone-600 mb-8">
          我們的團隊會盡快回覆您的問題，亦可協助您落單。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://wa.me/85260771493"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick(undefined, 'cta')}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#25D366] text-white font-bold rounded-full hover:bg-[#128C7E] transition-colors shadow-lg"
          >
            <Phone size={18} />
            WhatsApp 6077 1493
          </a>
          <a
            href="tel:+85260771493"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-stone-700 font-bold rounded-full border border-stone-200 hover:bg-stone-50 transition-colors"
          >
            <Phone size={18} />
            致電 6077 1493
          </a>
        </div>
      </div>
    </section>
  );

  const renderFooter = (s?: FooterSection) => {
    const fbUrl = s?.facebookUrl || 'https://www.facebook.com/profile.php?id=100064050482929';
    return (
      <footer id="contact" className="bg-stone-900 text-stone-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div className="lg:col-span-1">
              {s?.logoUrl ? (
                <img src={s.logoUrl} alt="阿力廚房 Alex Kitchen" className="mb-4 h-auto max-h-20 w-auto object-contain" />
              ) : (
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-white text-stone-400">
                  <ChefHat size={32} />
                </div>
              )}
              <p className="text-sm text-stone-300 leading-relaxed">
                Alex 主廚親自把關好食材，以優惠團購價將好東西帶俾一眾同好，滿 HK$350 起即可安排快遞直送。
              </p>
            </div>
            <div>
              <h3 className="font-bold text-white mb-4">聯絡我們</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 text-brand-400 flex-shrink-0" />
                  <span>{s?.address || '新界葵涌永健路16至20號高威工業中心A座501室'}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={16} className="text-brand-400 flex-shrink-0" />
                  <span>{s?.phone || '60771493'}</span>
                </li>
                <li className="flex items-center gap-3">
                  <MessageCircle size={16} className="text-brand-400 flex-shrink-0" />
                  <a
                    href={`https://wa.me/852${(s?.whatsapp || '60771493').replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick(undefined, 'footer')}
                    className="hover:text-brand-400 transition-colors"
                  >
                    WhatsApp {s?.whatsapp || '60771493'}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={16} className="text-brand-400 flex-shrink-0" />
                  <a href={`mailto:${s?.email || 'support@alexkitchens.com'}`} className="hover:text-brand-400 transition-colors">
                    {s?.email || 'support@alexkitchens.com'}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-white mb-4">追蹤我們</h3>
              <a
                href={fbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
              >
                {s?.facebookIcon ? (
                  <img src={s.facebookIcon} alt="Facebook" className="w-5 h-5" />
                ) : (
                  <Facebook size={18} />
                )}
                <span>Facebook</span>
              </a>
            </div>
            <div>
              <h3 className="font-bold text-white mb-4">快速連結</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/')}
                    className="text-stone-300 hover:text-white transition-colors"
                  >
                    立即選購
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/about')}
                    className="text-stone-300 hover:text-white transition-colors"
                  >
                    關於我們
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-stone-300 hover:text-white transition-colors"
                  >
                    回到頂部
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/terms-of-service')}
                    className="text-stone-300 hover:text-white transition-colors"
                  >
                    條款及細則
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="text-stone-300 hover:text-white transition-colors"
                  >
                    隱私條款
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-stone-700 text-center text-xs text-stone-400">
            Powered by{' '}
            <a
              href="https://jkdcoding.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: '#CCFF00' }}
            >
              JKDCoding
            </a>
          </div>
        </div>
      </footer>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] font-sans text-stone-800 pb-32">
      {/* ===== Header ===== */}
      <SiteHeader
        cartCount={menuItemCount}
        onCartClick={() => setStep('cart')}
      />

      {/* ===== Brand + Shop Landing (menu step) ===== */}
      {step === 'menu' && (
        <>
          {renderHero(heroSection)}
          {renderMission(missionSection)}
          {renderAbout(aboutSection)}
          {leverageSection && (
            <LeverageSection
              badge={leverageSection.badge}
              title={leverageSection.title}
              description={leverageSection.description}
              leftImageUrl={leverageSection.leftImageUrl}
              rightImageUrl={leverageSection.rightImageUrl}
              leftTitle={leverageSection.leftTitle}
              leftDescription={leverageSection.leftDescription}
              rightTitle={leverageSection.rightTitle}
              rightDescription={leverageSection.rightDescription}
              beforeLabel={leverageSection.beforeLabel}
              afterLabel={leverageSection.afterLabel}
            />
          )}

          {/* ===== Product Shop ===== */}
          <section id="shop" className="py-8 px-4 scroll-mt-24">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">1</div>
                <div>
                  <h3 className="text-2xl font-bold text-stone-800">主廚推介</h3>
                  <p className="text-sm text-stone-500">Alex 親自把關好食材</p>
                </div>

              </div>

              {!productsLoaded ? (
                <div className="text-center py-16">
                  <Loader2 className="animate-spin mx-auto text-[#C2410C]" size={40} />
                  <p className="mt-4 text-stone-400">載入中...</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-6 border-b border-stone-200">
                    {CATEGORY_TABS.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveCategory(tab.key)}
                        className={`pb-2 px-1 text-sm font-bold border-b-2 transition ${
                          activeCategory === tab.key
                            ? 'text-orange-600 border-orange-600'
                            : 'text-gray-500 border-transparent'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {activeProducts.filter(p => p.enabled).length === 0 ? (
                    <div className="text-center py-16 text-stone-400">暫無商品</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {activeProducts.filter(p => p.enabled).map(product => {
                        const qty = quantities[product.id] || 0;
                        const isLowStock = lowStockItems.has(product.id);
                        return (
                          <div
                            key={product.id}
                            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 overflow-hidden flex flex-col"
                          >
                            {/* Image */}
                            <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
                              {product.imageUrl ? (
                                <img
                                  src={product.imageUrl}
                                  alt={product.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
                                  <UtensilsCrossed size={40} className="text-orange-200" />
                                </div>
                              )}
                              {product.stockQuantity <= 0 && (
                                <span className="absolute top-3 right-3 bg-stone-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                                  已售罄
                                </span>
                              )}
                              {isLowStock && product.stockQuantity > 0 && (
                                <span className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                                  即將售罄
                                </span>
                              )}
                              {qty > 0 && (
                                <span className="absolute top-3 left-3 bg-[#C2410C] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                                  已選 {qty}
                                </span>
                              )}
                            </div>

                            {/* Info */}
                            <div className="p-5 flex flex-col flex-1">
                              <div className="flex justify-between items-start gap-2 mb-2">
                                <h4 className="text-lg font-bold text-stone-800 leading-tight">{product.name}</h4>
                                <span className="text-xl font-bold text-[#C2410C] whitespace-nowrap">HK${product.price}</span>
                              </div>
                              <p className="text-xs text-stone-500 leading-relaxed mb-4 flex-1">{linkifyDescription(product.description)}</p>

                              {/* Quantity Control */}
                              <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                                <span className="text-xs text-stone-400 font-medium">數量</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleQtyChange(product.id, -1)}
                                    className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center hover:bg-stone-200 active:scale-95 transition text-stone-600"
                                  >
                                    <Minus size={16} />
                                  </button>
                                  <input
                                    type="number"
                                    min={0}
                                    value={qty}
                                    onChange={e => setQtyDirect(product.id, parseInt(e.target.value) || 0)}
                                    className="w-12 h-9 text-center text-lg font-bold border border-stone-200 rounded-lg outline-none focus:border-[#C2410C] bg-white text-stone-800"
                                  />
                                  <button
                                    onClick={() => handleQtyChange(product.id, 1)}
                                    className="w-9 h-9 rounded-lg bg-[#FFF7ED] flex items-center justify-center hover:bg-orange-100 active:scale-95 transition text-[#C2410C]"
                                  >
                                    <Plus size={16} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {renderWhatsAppCta()}
          {renderFooter(footerSection)}
        </>
      )}

      {/* ===== Step: Cart Review ===== */}
      {step === 'cart' && (
        <section className="py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">2</div>
              <h3 className="text-2xl font-bold text-stone-800">我的訂單</h3>
            </div>

            {cart.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-stone-100">
                <ShoppingCart size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-lg text-stone-500 mb-4">購物車是空的</p>
                <button
                  onClick={() => setStep('menu')}
                  className="bg-[#C2410C] text-white text-lg font-bold py-3 px-8 rounded-xl hover:bg-[#EA580C] shadow-lg transition"
                >
                  返回選購
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {cart.map(item => (
                    <div key={item.productId} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="text-lg font-bold text-stone-800">{item.name}</div>
                        <div className="text-sm text-stone-500 mt-0.5">HK${item.unitPrice} / 份</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCartQtyChange(item.productId, -1)}
                          className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center hover:bg-stone-200"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-lg font-bold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleCartQtyChange(item.productId, 1)}
                          className="w-8 h-8 bg-[#FFF7ED] rounded-lg flex items-center justify-center hover:bg-orange-100"
                        >
                          <Plus size={14} className="text-[#C2410C]" />
                        </button>
                      </div>
                      <div className="text-right min-w-[80px]">
                        <div className="text-xl font-bold text-[#C2410C]">HK${item.subtotal}</div>
                      </div>
                      <button
                        onClick={() => removeCartItem(item.productId)}
                        className="text-stone-400 hover:text-red-500 transition p-1"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#FFF7ED] to-amber-50 rounded-xl p-6 mb-6 border border-orange-100">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-stone-800">總計</span>
                    <span className="text-3xl font-bold text-[#C2410C]">HK${cartTotal}</span>
                  </div>
                  {minOrderAmount > 0 && cartTotal < minOrderAmount && (
                    <div className="mt-3 flex items-center gap-2 text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded-lg">
                      <span>⚠️</span>
                      <span>尚差 HK${minOrderAmount - cartTotal} 達到最低消費 HK${minOrderAmount}</span>
                    </div>
                  )}
                  {(minOrderAmount === 0 || cartTotal >= minOrderAmount) && (
                    <div className="mt-3 flex items-center gap-2 text-green-600 text-sm font-medium bg-green-50 px-3 py-2 rounded-lg">
                      <CheckCircle size={16} />
                      <span>{minOrderAmount > 0 ? '已達最低消費，可以結賬' : '可以結賬'}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBackToMenu}
                    className="flex-1 bg-white border-2 border-stone-200 text-stone-700 text-lg font-bold py-4 rounded-xl hover:bg-stone-50 transition"
                  >
                    繼續選購
                  </button>
                  <button
                    onClick={() => setStep('delivery')}
                    disabled={minOrderAmount > 0 && cartTotal < minOrderAmount}
                    className={`flex-[2] text-lg font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 transition ${
                      minOrderAmount === 0 || cartTotal >= minOrderAmount
                        ? 'bg-[#C2410C] text-white hover:bg-[#EA580C]'
                        : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                    }`}
                  >
                    填寫送貨資料
                    <ChevronRight size={20} />
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ===== Step: Delivery Form ===== */}
      {step === 'delivery' && cart.length > 0 && (
        <section className="py-8 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C2410C] rounded-full flex items-center justify-center text-white text-lg font-bold shadow-lg">3</div>
              <h3 className="text-2xl font-bold text-stone-800">送貨資料</h3>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 md:p-8 space-y-5">
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">送貨地區 *</label>
                <select
                  name="region"
                  required
                  value={delivery.region}
                  onChange={handleDeliveryChange}
                  className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition bg-white"
                >
                  <option value="">請選擇送貨地區</option>
                  <option value="HK_ISLAND">香港島</option>
                  <option value="KOWLOON">九龍</option>
                  <option value="NT">新界</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">詳細地址（大廈名稱/座數、樓層、單位） *</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    name="addressBuilding"
                    placeholder="大廈名稱/座數"
                    required
                    value={delivery.addressBuilding}
                    onChange={handleDeliveryChange}
                    className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition"
                  />
                  <input
                    type="text"
                    name="addressFloor"
                    placeholder="樓層"
                    required
                    value={delivery.addressFloor}
                    onChange={handleDeliveryChange}
                    className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition"
                  />
                  <input
                    type="text"
                    name="addressUnit"
                    placeholder="單位"
                    required
                    value={delivery.addressUnit}
                    onChange={handleDeliveryChange}
                    className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">送貨日期</label>
                <input
                  type="date"
                  name="deliveryDate"
                  required
                  min={minDateStr}
                  value={delivery.deliveryDate}
                  onChange={handleDeliveryChange}
                  className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition"
                />
                <p className="text-sm text-[#C2410C] mt-1.5 font-medium">需提前 2 天預訂</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">聯絡人姓名</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={delivery.name}
                  onChange={handleDeliveryChange}
                  className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">聯絡電話（送貨聯絡用） *</label>
                <input
                  type="tel"
                  name="contactPhone"
                  inputMode="tel"
                  placeholder="例如：91234567"
                  required
                  value={delivery.contactPhone}
                  onChange={handleDeliveryChange}
                  className={`w-full p-4 text-base border rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition ${
                    deliveryPhoneError ? 'border-red-300 bg-red-50' : 'border-stone-200'
                  }`}
                />
                {deliveryPhoneError && (
                  <p className="text-sm text-red-600 mt-1.5 font-medium">{deliveryPhoneError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">備註（可選）</label>
                <textarea
                  name="remarks"
                  rows={3}
                  placeholder="如有特別要求請註明"
                  value={delivery.remarks}
                  onChange={handleDeliveryChange}
                  className="w-full p-4 text-base border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] outline-none transition resize-none"
                />
              </div>

              {submitError && (
                <p className="text-base text-red-600 bg-red-50 p-4 rounded-lg text-center">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                onClick={() => trackWhatsAppClick(undefined, 'order_submit')}
                className="w-full bg-[#25D366] text-white text-lg font-bold py-4 rounded-xl hover:bg-[#128C7E] shadow-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isSubmitting ? (
                  <><Loader2 className="inline animate-spin mr-2" size={24} /> 提交中...</>
                ) : (
                  <>
                    <MessageCircle size={24} />
                    <span className="flex flex-col items-center leading-tight">
                      <span>WhatsApp 確認訂單</span>
                      <span>確認（HK${cartTotal}）</span>
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* ===== Fixed Bottom Bar (Menu Step) ===== */}
      {step === 'menu' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-stone-200 shadow-[0_-4px_24px_rgba(0,0,0,0.08)] z-50">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-stone-800">HK${menuTotal}</span>
                <span className="text-sm text-stone-500">/ {menuItemCount} 件</span>
              </div>
              {minOrderAmount > 0 && shortfall > 0 ? (
                <p className="text-sm text-red-600 font-medium">尚差 HK${shortfall} 達最低消費 HK${minOrderAmount}</p>
              ) : minOrderAmount > 0 && menuItemCount > 0 ? (
                <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                  <CheckCircle size={14} /> 已達最低消費
                </p>
              ) : menuItemCount > 0 ? (
                <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                  <CheckCircle size={14} /> 可結賬
                </p>
              ) : (
                <p className="text-sm text-stone-400">請選擇商品</p>
              )}
            </div>
            <button
              onClick={handleGoToCart}
              disabled={menuItemCount === 0 || (minOrderAmount > 0 && shortfall > 0)}
              className={`px-6 py-3 rounded-xl text-lg font-bold shadow flex items-center gap-2 transition ${
                menuItemCount > 0 && !(minOrderAmount > 0 && shortfall > 0)
                  ? 'bg-[#C2410C] text-white hover:bg-[#EA580C]'
                  : 'bg-stone-300 text-stone-500 cursor-not-allowed'
              }`}
            >
              去結賬 <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLanding;
